import React, { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import SystemDataService from '../../Services/SystemDataService';
import { getOurWorkData } from '../../Public/our-work-data';
import useHandleRedirect from '../../Hooks/useHandleRedirect';
import MediaComponent from '../Media/media-component';
import GalleryLightbox from '../Gallery/gallery-lightbox-component';
import { ButtonSecondary } from '../Buttons/button-component';

/* ── Icons ───────────────────────────────────────────────────────────────── */
// Projects-completed, a home with a check mark.
function ProjectsIcon({ className = 'h-6 w-6' }) {
    return (<svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"><path d="M3 10.5 12 3l9 7.5" /><path d="M5 9.5V20h14V9.5" /><path d="m9 13.5 2 2 4-4" /></svg>);
}
// Years-of-experience, an award medal.
function ExperienceIcon({ className = 'h-6 w-6' }) {
    return (<svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="9" r="6" /><path d="M8.5 13.5 7 21l5-3 5 3-1.5-7.5" /></svg>);
}
function ArrowIcon({ className = 'h-5 w-5' }) {
    return (<svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2}><path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" /></svg>);
}

/* ── Tiles ───────────────────────────────────────────────────────────────── */
const ASPECTS = ['aspect-[4/5]', 'aspect-[4/3]', 'aspect-square', 'aspect-[3/4]', 'aspect-[5/4]'];

function ImageTile({ src, aspect, delay = 0, onClick }) {
    return (
        <motion.button
            type="button"
            onClick={onClick}
            className={`group mb-3 block w-full break-inside-avoid rounded-2xl overflow-hidden ring-1 ring-white/10 cursor-zoom-in ${aspect}`}
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.5, delay }}
        >
            <MediaComponent
                src={src}
                alt=""
                width={600}
                quality={65}
                useSekeleton={false}
                wrapperClassName="w-full h-full"
                className="w-full h-full transition-transform duration-500 group-hover:scale-105"
            />
        </motion.button>
    );
}

function StatTile({ variant, value, label }) {
    const isMembers = variant === 'members';
    return (
        <motion.div
            className={`mb-3 break-inside-avoid rounded-2xl p-5 flex flex-col gap-3 ${isMembers ? 'bg-secondary-500 text-white' : 'bg-accent-500 text-primary-950'}`}
            initial={{ opacity: 0, scale: 0.94 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45 }}
        >
            <span className={`h-11 w-11 rounded-full flex items-center justify-center ${isMembers ? 'bg-white/20 text-white' : 'bg-black/10 text-primary-950'}`}>
                {isMembers ? <ProjectsIcon className="h-6 w-6" /> : <ExperienceIcon className="h-6 w-6" />}
            </span>
            <div className="leading-none">
                <div className="font-secondary font-extralight text-4xl md:text-5xl">{value}</div>
                <div className="font-primary text-xs font-medium tracking-wide uppercase mt-2 opacity-90">{label}</div>
            </div>
        </motion.div>
    );
}

function OurWorkSection() {
    const [galleryImages, setGalleryImages] = useState([]);
    const [lightboxIndex, setLightboxIndex] = useState(null);

    const handleRedirect = useHandleRedirect();
    const { language = 'en', dir = 'rtl' } = useSelector((state) => state.systemData || {});
    const ui = useMemo(() => getOurWorkData(language).ui, [language]);

    // Prefer live gallery images; fall back to the static placeholders in data.
    useEffect(() => {
        let mounted = true;
        SystemDataService.getGalleryImages()
            .then((gallery) => {
                if (!mounted) return;
                const urls = (gallery || [])
                    .map((item) => (typeof item === 'string' ? item : item.media || item.url || item.image))
                    .filter(Boolean);
                if (urls.length) setGalleryImages(urls);
            })
            .catch(() => { });
        return () => { mounted = false; };
    }, []);

    const images = galleryImages.length ? galleryImages : (ui.images || []);

    // Build the mosaic: image tiles with the two stat cards interleaved into the flow.
    const tiles = useMemo(() => {
        const list = images.map((src, i) => ({ type: 'image', src, imgIndex: i, aspect: ASPECTS[i % ASPECTS.length], key: `img-${i}` }));
        const membersAt = Math.min(3, list.length);
        const eventsAt = Math.min(6, list.length + 1);
        list.splice(membersAt, 0, { type: 'members', key: 'stat-members' });
        list.splice(eventsAt, 0, { type: 'events', key: 'stat-events' });
        return list;
    }, [images]);

    return (
        <section dir={dir} className="w-full bg-primary-950 py-14 md:py-20 overflow-hidden">
            <div className="max-w-[1100px] mx-auto px-4 md:px-6">

                {/* Header */}
                <div className="flex flex-col items-center text-center mb-10">
                    <h2 className="font-secondary font-extrabold text-white leading-[0.95] tracking-[-0.02em] text-4xl md:text-5xl lg:text-6xl">
                        {ui.titleMain}
                    </h2>
                    <p className="text-white/70 text-base md:text-lg mt-4 max-w-2xl">{ui.subtitle}</p>
                </div>

                {/* Mosaic */}
                {images.length === 0 ? (
                    <div className="py-20 text-center text-white/30 font-bold uppercase">
                        {ui.noImagesText}
                    </div>
                ) : (
                    <div className="columns-2 md:columns-3 lg:columns-4 gap-3">
                        {tiles.map((t, i) =>
                            t.type === 'image' ? (
                                <ImageTile key={t.key} src={t.src} aspect={t.aspect} delay={(i % 4) * 0.05} onClick={() => setLightboxIndex(t.imgIndex)} />
                            ) : t.type === 'members' ? (
                                <StatTile key={t.key} variant="members" value={ui.membersStat.value} label={ui.membersStat.label} />
                            ) : (
                                <StatTile key={t.key} variant="events" value={ui.eventsStat.value} label={ui.eventsStat.label} />
                            )
                        )}
                    </div>
                )}

                {/* See more */}
                <div className="mt-8 w-full max-w-md mx-auto">
                    <ButtonSecondary
                        size="lg"
                        fullWidth
                        href="/our-work"
                        icon={<ArrowIcon className="h-5 w-5" />}
                    >
                        {ui.seeMoreText}
                    </ButtonSecondary>
                </div>
            </div>

            <GalleryLightbox
                images={images.map((src) => ({ src }))}
                index={lightboxIndex}
                onClose={() => setLightboxIndex(null)}
                onPrev={() => setLightboxIndex((i) => (i - 1 + images.length) % images.length)}
                onNext={() => setLightboxIndex((i) => (i + 1) % images.length)}
            />
        </section>
    );
}

export default OurWorkSection;
